/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

/* eslint-disable @typescript-eslint/no-explicit-any */
import { TParams } from '@global-types/index';
import { TStore } from '@store/index';
import dayjs from 'dayjs';
import { StateCreator } from 'zustand';
import {
	TImportHistoryModified,
	TImportHistoryQueryParams,
	TImportHistorySortParams,
} from '../types';

/* -------------------------------------------------------------------------- */
/*                                  Interface                                 */
/* -------------------------------------------------------------------------- */

export interface IImportHistorySlice {
	/************* Import history **************/
	importHistoriesPage: number;
	importHistoriesTotal: number;
	importHistoriesPageSize: number;
	importHistoriesQueryParams: TParams;
	importHistoriesSortParams: any;
	importHistorySelected: any;
	/************************************/
	importHistoriesLinesPage: number;
	importHistoriesLinesTotal: number;
	importHistoriesLinesPageSize: number;
	importHistoriesLinesQueryParams: TParams;
	importHistoriesLinesSortParams: any;
	importHistoryLinesSelected: Partial<TImportHistoryModified>;
	/************************************/
	enableRollback: boolean;
	productionLineSelected: any;
	/************************************/
	importHistoriesHasVerifiedOrMintedProduction: boolean;
	setImportHistoriesHasVerifiedOrMintedProduction: (
		_importHistoriesHasVerifiedOrMintedProduction: boolean
	) => void;

	setProductionLineSelected: (
		_productionLineSelected: any
	) => void;

	setEnableRollback: (_enableRollback: boolean) => void;

	setImportHistorySelected: (
		_importHistory: Partial<TImportHistoryModified>
	) => void;
	setImportHistoriesPage: (
		_importHistoriesPage: number
	) => void;
	setImportHistoriesQueryParams: (
		_importHistoriesQueryParams: any
	) => void;
	setImportHistoriesSortParams: (
		_importHistoriesSortParams: any
	) => void;
	setImportHistoriesPageSize: (_pageSize: any) => void;
	/************************************/

	setImportHistoryLinesSelected: (
		_importHistory: Partial<TImportHistoryModified>
	) => void;
	setImportHistoriesLinesPage: (
		_importHistoriesPage: number
	) => void;
	setImportHistoriesLinesQueryParams: (
		_importHistoriesQueryParams: any
	) => void;
	setImportHistoriesLinesSortParams: (
		_importHistoriesSortParams: any
	) => void;
	setImportHistoriesLinesPageSize: (
		_pageSize: any
	) => void;
}

const initialState = {
	importHistoriesTotal: 0,
	importHistoriesPage: 1,
	importHistoriesPageSize: 100,
	importHistoriesQueryParams: {
		createdAt: {
			$gte: dayjs()
				.subtract(1, 'week')
				.format('YYYY-MM-DD'),
			$lt: dayjs().add(1, 'day').format('YYYY-MM-DD'),
		},
	},
	importHistoriesSortParams: {},
	importHistorySelected: {},
	//Lines
	importHistoriesLinesTotal: 0,
	importHistoriesLinesPage: 0,
	importHistoriesLinesPageSize: 0,
	importHistoriesLinesQueryParams: {},
	importHistoriesLinesSortParams: '-createdAt',
	importHistoryLinesSelected: {},
	importHistoriesHasVerifiedOrMintedProduction: false,
	productionLineSelected: {},

	enableRollback: false,
};

export const createImportHistorySlice: StateCreator<
	TStore,
	[],
	[],
	IImportHistorySlice
> = (set) => ({
	...initialState,
	setImportHistoriesHasVerifiedOrMintedProduction: (
		_importHistoriesHasVerifiedOrMintedProduction: boolean
	) =>
		set({
			importHistoriesHasVerifiedOrMintedProduction:
				_importHistoriesHasVerifiedOrMintedProduction,
		}),

	setProductionLineSelected: (
		_productionLineSelected: any
	) =>
		set({
			productionLineSelected: _productionLineSelected,
		}),

	setEnableRollback: (_enableRollback: boolean) =>
		set({ enableRollback: _enableRollback }),

	setImportHistorySelected: (
		_importHistory: Partial<TImportHistoryModified>
	) => set({ importHistorySelected: _importHistory }),
	setImportHistoriesPage: (_page: number) =>
		set({ importHistoriesPage: _page }),
	setImportHistoriesPageSize: (_pageSize: number) =>
		set({ importHistoriesPageSize: _pageSize }),
	setImportHistoriesSortParams: (
		_sortStatus: TImportHistorySortParams
	) => set({ importHistoriesSortParams: _sortStatus }),
	setImportHistoriesQueryParams: (
		_queryParams: TImportHistoryQueryParams
	) => set({ importHistoriesQueryParams: _queryParams }),

	setImportHistoryLinesSelected: (
		_importHistory: Partial<TImportHistoryModified>
	) =>
		set({ importHistoryLinesSelected: _importHistory }),

	setImportHistoriesLinesPage: (_page: number) =>
		set({ importHistoriesLinesPage: _page }),
	setImportHistoriesLinesPageSize: (_pageSize: number) =>
		set({ importHistoriesLinesPageSize: _pageSize }),
	setImportHistoriesLinesSortParams: (
		_sortStatus: TImportHistorySortParams
	) =>
		set({ importHistoriesLinesSortParams: _sortStatus }),
	setImportHistoriesLinesQueryParams: (
		_queryParams: TImportHistoryQueryParams
	) =>
		set({
			importHistoriesLinesQueryParams: _queryParams,
		}),
});
